import React, {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {RSS_GET_ALL_POSTS_URL} from '../constants';
import {resolvePostImageSrc} from '../posts/imageUrl';

type BlogPost = {
  id: string;
  author?: string;
  title: string;
  description: string;
  pubDate: string;
  image: string;
};

const fmtDateTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const BlogList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(RSS_GET_ALL_POSTS_URL);
        const text = await res.text();
        if (!res.ok) {
          if (!cancelled) setError(text.slice(0, 200) || `Request failed (${res.status})`);
          return;
        }
        const json = JSON.parse(text) as {data?: Array<Record<string, unknown>>};
        const rows = Array.isArray(json.data) ? json.data : [];
        const mapped: BlogPost[] = rows.map((row) => ({
          id: String(row.id ?? ''),
          author: typeof row.author === 'string' ? row.author : undefined,
          title: typeof row.title === 'string' ? row.title : '',
          description: typeof row.description === 'string' ? row.description : '',
          pubDate: typeof row.pubDate === 'string' ? row.pubDate : '',
          image: resolvePostImageSrc(typeof row.image === 'string' ? row.image : ''),
        }));
        if (!cancelled) setPosts(mapped);
      } catch {
        if (!cancelled) setError('Network error. Could not load blog posts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = useMemo(() => {
    return [...posts].sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
  }, [posts]);

  return (
    <div className="posts-page">
      <div className="posts-inner">
        <header className="posts-top">
          <button type="button" className="posts-back" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <Link to="/" className="posts-home-link">
            CriticalMinerals.News
          </Link>
        </header>

        <h1 className="posts-title">Blog</h1>

        {error ? <p className="posts-delete-banner">{error}</p> : null}

        <ul className="posts-list">
          {loading && sorted.length === 0 ? (
            <li className="posts-empty">Loading posts…</li>
          ) : sorted.length === 0 ? (
            <li className="posts-empty">No blog posts yet.</li>
          ) : (
            sorted.map((p) => (
              <li
                key={p.id}
                className="posts-item"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/blog/${encodeURIComponent(p.id)}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/blog/${encodeURIComponent(p.id)}`);
                }}
                style={{cursor: 'pointer'}}
              >
                <div className="posts-item-img-wrap">
                  {p.image ? (
                    <img className="posts-item-img" src={resolvePostImageSrc(p.image)} alt="" loading="lazy" />
                  ) : (
                    <div className="posts-item-img-fallback" aria-hidden>
                      No image
                    </div>
                  )}
                </div>
                <div className="posts-item-body">
                  <h2 className="posts-item-title">{p.title || 'Untitled'}</h2>
                  {p.author ? <div className="posts-item-author">{p.author}</div> : null}
                  <p className="posts-item-desc">{(p.description || '').slice(0, 220)}</p>
                  <time className="posts-item-date" dateTime={p.pubDate}>
                    {fmtDateTime(p.pubDate)}
                  </time>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default BlogList;

